import { MessageOutlined, SendOutlined, ToolOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { Button, Collapse, Input, Spin, Tag, Typography, theme } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';

import { mcpAllowedScopes } from '@clh/mcp-scopes';
import { unwrapError, useConfig } from '@clh/ui';

import { useToast } from '../hooks/use-toast';

const { TextArea } = Input;
const { Text } = Typography;

interface ToolCall {
    id: string;
    name: string;
    input: Record<string, unknown>;
    result?: string;
}

interface ChatMessage {
    role: 'user' | 'assistant';
    content: string;
    toolCalls?: ToolCall[];
}

export default function Chat() {
    const config = useConfig();
    const { getAccessTokenSilently } = useAuth0();
    const [chatToken, setChatToken] = useState<string | null>(null);
    const { notification } = useToast();

    useEffect(() => {
        getAccessTokenSilently({
            authorizationParams: {
                audience: config.apiAuth0Audience as string,
                scope: mcpAllowedScopes.join(' '),
            },
        })
            .then(setChatToken)
            .catch((err) => {
                notification.error({
                    message: unwrapError(err),
                    duration: 3,
                });
            });
    }, [getAccessTokenSilently, config.apiAuth0Audience]);

    const {
        token: { colorBgContainer, borderRadiusLG, colorPrimary },
    } = theme.useToken();
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const textAreaRef = useRef<ReturnType<typeof Input.TextArea> | null>(null);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    const sendMessage = async () => {
        const trimmed = input.trim();
        if (!trimmed || isStreaming || !chatToken) return;

        const userMessage: ChatMessage = { role: 'user', content: trimmed };
        const updatedMessages = [...messages, userMessage];
        setMessages(updatedMessages);
        setInput('');
        setIsStreaming(true);

        const assistantIndex = updatedMessages.length;
        setMessages((prev) => [
            ...prev,
            { role: 'assistant', content: '', toolCalls: [] },
        ]);

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        try {
            const apiMessages = updatedMessages.map((m) => ({
                role: m.role,
                content: m.content,
            }));

            const response = await fetch(`${config.apiBaseUrl}/admin-chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${chatToken}`,
                },
                body: JSON.stringify({ messages: apiMessages }),
                signal: abortController.signal,
            });

            if (!response.ok) {
                throw new Error(
                    `HTTP ${response.status}: ${response.statusText}`
                );
            }

            const reader = response.body?.getReader();
            if (!reader) throw new Error('No response body');

            const decoder = new TextDecoder();
            let buffer = '';
            let toolCallCounter = 0;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });

                const parts = buffer.split('\n\n');
                buffer = parts.pop() ?? '';

                for (const part of parts) {
                    if (!part.trim()) continue;

                    const lines = part.split('\n');
                    let eventType = '';
                    let eventData = '';

                    for (const line of lines) {
                        if (line.startsWith('event: ')) {
                            eventType = line.slice(7);
                        } else if (line.startsWith('data: ')) {
                            eventData = line.slice(6);
                        }
                    }

                    if (!eventType || !eventData) continue;

                    const data = JSON.parse(eventData);

                    switch (eventType) {
                        case 'text_delta':
                            setMessages((prev) => {
                                const updated = [...prev];
                                const assistant = updated[assistantIndex];
                                if (assistant) {
                                    updated[assistantIndex] = {
                                        ...assistant,
                                        content: assistant.content + data.text,
                                    };
                                }
                                return updated;
                            });
                            break;

                        case 'tool_call_start': {
                            const callId = `tc-${++toolCallCounter}`;
                            setMessages((prev) => {
                                const updated = [...prev];
                                const assistant = updated[assistantIndex];
                                if (assistant) {
                                    updated[assistantIndex] = {
                                        ...assistant,
                                        toolCalls: [
                                            ...(assistant.toolCalls ?? []),
                                            {
                                                id: callId,
                                                name: data.name,
                                                input: data.input,
                                            },
                                        ],
                                    };
                                }
                                return updated;
                            });
                            break;
                        }

                        case 'tool_call_result':
                            setMessages((prev) => {
                                const updated = [...prev];
                                const assistant = updated[assistantIndex];
                                if (assistant?.toolCalls) {
                                    const calls = [...assistant.toolCalls];
                                    const lastCallOfName = calls
                                        .slice()
                                        .reverse()
                                        .find((tc) => tc.name === data.name);
                                    if (lastCallOfName) {
                                        lastCallOfName.result = data.result;
                                    }
                                    updated[assistantIndex] = {
                                        ...assistant,
                                        toolCalls: calls,
                                    };
                                }
                                return updated;
                            });
                            break;

                        case 'error':
                            setMessages((prev) => {
                                const updated = [...prev];
                                const assistant = updated[assistantIndex];
                                if (assistant) {
                                    updated[assistantIndex] = {
                                        ...assistant,
                                        content:
                                            assistant.content +
                                            `\n\nError: ${data.message}`,
                                    };
                                }
                                return updated;
                            });
                            break;
                    }
                }
            }
        } catch (error) {
            if ((error as Error).name !== 'AbortError') {
                const errorMessage =
                    error instanceof Error ? error.message : 'Unknown error';
                setMessages((prev) => {
                    const updated = [...prev];
                    const assistant = updated[assistantIndex];
                    if (assistant) {
                        updated[assistantIndex] = {
                            ...assistant,
                            content:
                                assistant.content +
                                `\n\nError: ${errorMessage}`,
                        };
                    }
                    return updated;
                });
            }
        } finally {
            setIsStreaming(false);
            abortControllerRef.current = null;
            requestAnimationFrame(() => {
                (textAreaRef.current as any)?.focus();
            });
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
                width: '100%',
                background: '#f0f2f5',
            }}
        >
            {/* Messages area */}
            <div
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '24px 24px 0',
                }}
            >
                <div style={{ maxWidth: 800, margin: '0 auto' }}>
                    {messages.length === 0 && (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 'calc(100vh - 140px)',
                                gap: 12,
                            }}
                        >
                            <MessageOutlined
                                style={{ fontSize: 48, color: '#bbb' }}
                            />
                            <Text type="secondary" style={{ fontSize: 16 }}>
                                Ask a question about Humla platform data
                            </Text>
                            <Text type="secondary" style={{ fontSize: 13 }}>
                                e.g. "How many active profiles are there?" or
                                "Show me upcoming shifts"
                            </Text>
                        </div>
                    )}

                    {messages.map((msg, idx) => (
                        <div
                            key={msg.role + idx.toString()}
                            style={{
                                display: 'flex',
                                justifyContent:
                                    msg.role === 'user'
                                        ? 'flex-end'
                                        : 'flex-start',
                                marginBottom: 16,
                            }}
                        >
                            {msg.role === 'user' ? (
                                <div
                                    style={{
                                        maxWidth: '75%',
                                        padding: '10px 16px',
                                        borderRadius: borderRadiusLG,
                                        background: colorPrimary,
                                        color: '#fff',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        lineHeight: 1.6,
                                    }}
                                >
                                    {msg.content}
                                </div>
                            ) : (
                                <div
                                    style={{
                                        maxWidth: '85%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 8,
                                    }}
                                >
                                    {/* Tool calls */}
                                    {msg.toolCalls &&
                                        msg.toolCalls.length > 0 && (
                                            <Collapse
                                                size="small"
                                                style={{
                                                    background:
                                                        colorBgContainer,
                                                }}
                                                items={msg.toolCalls.map(
                                                    (tc) => ({
                                                        key: tc.id,
                                                        label: (
                                                            <span
                                                                style={{
                                                                    display:
                                                                        'flex',
                                                                    alignItems:
                                                                        'center',
                                                                    gap: 6,
                                                                }}
                                                            >
                                                                <ToolOutlined
                                                                    style={{
                                                                        fontSize: 12,
                                                                        color: '#888',
                                                                    }}
                                                                />
                                                                <code
                                                                    style={{
                                                                        fontSize: 12,
                                                                    }}
                                                                >
                                                                    {tc.name}
                                                                </code>
                                                                {tc.result !==
                                                                undefined ? (
                                                                    <Tag
                                                                        color="green"
                                                                        style={{
                                                                            fontSize: 11,
                                                                            lineHeight:
                                                                                '18px',
                                                                            marginLeft: 4,
                                                                        }}
                                                                    >
                                                                        done
                                                                    </Tag>
                                                                ) : (
                                                                    <Spin
                                                                        size="small"
                                                                        style={{
                                                                            marginLeft: 4,
                                                                        }}
                                                                    />
                                                                )}
                                                            </span>
                                                        ),
                                                        children: (
                                                            <div
                                                                style={{
                                                                    display:
                                                                        'flex',
                                                                    flexDirection:
                                                                        'column',
                                                                    gap: 8,
                                                                }}
                                                            >
                                                                <div>
                                                                    <Text
                                                                        type="secondary"
                                                                        style={{
                                                                            fontSize: 11,
                                                                            textTransform:
                                                                                'uppercase',
                                                                            letterSpacing: 0.5,
                                                                        }}
                                                                    >
                                                                        Input
                                                                    </Text>
                                                                    <pre
                                                                        style={{
                                                                            fontSize: 12,
                                                                            maxHeight: 150,
                                                                            overflow:
                                                                                'auto',
                                                                            background:
                                                                                '#f5f5f5',
                                                                            padding: 8,
                                                                            borderRadius: 4,
                                                                            margin: '4px 0 0',
                                                                        }}
                                                                    >
                                                                        {JSON.stringify(
                                                                            tc.input,
                                                                            null,
                                                                            2
                                                                        )}
                                                                    </pre>
                                                                </div>
                                                                {tc.result !==
                                                                    undefined && (
                                                                    <div>
                                                                        <Text
                                                                            type="secondary"
                                                                            style={{
                                                                                fontSize: 11,
                                                                                textTransform:
                                                                                    'uppercase',
                                                                                letterSpacing: 0.5,
                                                                            }}
                                                                        >
                                                                            Result
                                                                        </Text>
                                                                        <pre
                                                                            style={{
                                                                                fontSize: 12,
                                                                                maxHeight: 250,
                                                                                overflow:
                                                                                    'auto',
                                                                                background:
                                                                                    '#f5f5f5',
                                                                                padding: 8,
                                                                                borderRadius: 4,
                                                                                margin: '4px 0 0',
                                                                            }}
                                                                        >
                                                                            {
                                                                                tc.result
                                                                            }
                                                                        </pre>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ),
                                                    })
                                                )}
                                            />
                                        )}

                                    {/* Text content */}
                                    {(msg.content ||
                                        !(
                                            isStreaming &&
                                            idx === messages.length - 1
                                        )) && (
                                        <div
                                            style={{
                                                padding: '10px 16px',
                                                borderRadius: borderRadiusLG,
                                                background: colorBgContainer,
                                                wordBreak: 'break-word',
                                                lineHeight: 1.6,
                                            }}
                                        >
                                            <Markdown>{msg.content}</Markdown>
                                        </div>
                                    )}

                                    {/* Streaming indicator */}
                                    {isStreaming &&
                                        idx === messages.length - 1 &&
                                        !msg.content && (
                                            <div
                                                style={{
                                                    padding: '10px 16px',
                                                    borderRadius:
                                                        borderRadiusLG,
                                                    background:
                                                        colorBgContainer,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                }}
                                            >
                                                <Spin size="small" />
                                                <Text type="secondary">
                                                    {msg.toolCalls &&
                                                    msg.toolCalls.length > 0
                                                        ? 'Processing...'
                                                        : 'Thinking...'}
                                                </Text>
                                            </div>
                                        )}
                                </div>
                            )}
                        </div>
                    ))}
                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Input area */}
            <div
                style={{
                    padding: '16px 24px 24px',
                    background: '#f0f2f5',
                }}
            >
                <div
                    style={{
                        maxWidth: 800,
                        margin: '0 auto',
                        display: 'flex',
                        gap: 8,
                        background: colorBgContainer,
                        padding: 8,
                        borderRadius: borderRadiusLG,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    }}
                >
                    <TextArea
                        ref={textAreaRef as any}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask about profiles, shifts, facilities..."
                        autoSize={{ minRows: 1, maxRows: 4 }}
                        disabled={isStreaming}
                        variant="borderless"
                        style={{ flex: 1, resize: 'none' }}
                    />
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={sendMessage}
                        disabled={isStreaming || !input.trim()}
                        style={{ alignSelf: 'flex-end' }}
                    />
                </div>
            </div>
        </div>
    );
}
